import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Users, Boxes, Flag, ShieldOff, ShieldCheck, BadgeCheck, Loader2, MessagesSquare } from "lucide-react";

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [reports, setReports] = useState([]);
  const [audit, setAudit] = useState([]);
  const [q, setQ] = useState("");
  const [listingFilter, setListingFilter] = useState("all"); // all | pending_review | active | removed
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) return nav("/login", { state: { redirect: "/admin" } });
    if (!user.is_admin) {
      toast.error("Admin access required");
      nav("/");
    }
  }, [user, loading, nav]);

  const loadOverview = async () => {
    const r = await api.get("/admin/stats");
    setStats(r.data);
  };
  const loadUsers = async () => {
    const r = await api.get("/admin/users", { params: q ? { q } : {} });
    setUsers(r.data || []);
  };
  const loadListings = async () => {
    const params = {};
    if (q) params.q = q;
    if (listingFilter !== "all") params.status = listingFilter;
    const r = await api.get("/admin/listings", { params });
    setListings(r.data || []);
  };
  const loadReports = async () => {
    const r = await api.get("/admin/reports");
    setReports(r.data || []);
  };
  const loadAudit = async () => {
    const r = await api.get("/admin/audit");
    setAudit(r.data || []);
  };

  useEffect(() => {
    if (!user?.is_admin) return;
    if (tab === "overview") loadOverview();
    if (tab === "users") loadUsers();
    if (tab === "listings") loadListings();
    if (tab === "reports") loadReports();
    if (tab === "audit") loadAudit();
    // eslint-disable-next-line
  }, [tab, user, listingFilter]);

  const toggleSuspend = async (u) => {
    setWorking(true);
    try {
      const r = await api.post(`/admin/users/${u.user_id}/suspend`);
      toast.success(r.data.is_suspended ? `${u.name} suspended` : `${u.name} reinstated`);
      await loadUsers();
    } finally {
      setWorking(false);
    }
  };
  const toggleVerify = async (u) => {
    setWorking(true);
    try {
      const r = await api.post(`/admin/users/${u.user_id}/verify`);
      toast.success(r.data.is_verified ? `${u.name} verified` : `Verification removed`);
      await loadUsers();
    } finally {
      setWorking(false);
    }
  };
  const setListingStatus = async (l, status, reason = null) => {
    setWorking(true);
    try {
      await api.post(`/admin/listings/${l.listing_id}/status`, reason ? { status, reason } : { status });
      toast.success(`Listing ${status === "active" ? "approved" : status}`);
      await loadListings();
    } finally {
      setWorking(false);
    }
  };
  const confirmReject = async () => {
    if (!rejectingId) return;
    if (!rejectReason.trim()) return toast.error("Please provide a reason");
    const l = listings.find((x) => x.listing_id === rejectingId);
    if (!l) return;
    await setListingStatus(l, "removed", rejectReason.trim());
    setRejectingId(null);
    setRejectReason("");
  };
  const resolveReport = async (r) => {
    setWorking(true);
    try {
      await api.post(`/admin/reports/${r.report_id}/resolve`);
      toast.success("Report resolved");
      await loadReports();
    } finally {
      setWorking(false);
    }
  };

  if (loading) {
    return <div className="pbp-section py-24 text-center text-[#86868b]">Loading…</div>;
  }
  if (!user) {
    return (
      <div className="pbp-section py-24 text-center" data-testid="admin-no-auth">
        <h2 className="text-2xl font-semibold">Sign in required</h2>
        <p className="mt-2 text-[#86868b]">Redirecting you to sign in…</p>
      </div>
    );
  }
  if (!user.is_admin) {
    return (
      <div className="pbp-section py-24 text-center" data-testid="admin-no-perm">
        <h2 className="text-2xl font-semibold">Admin access required</h2>
        <p className="mt-2 text-[#86868b]">You do not have admin permissions for this workspace.</p>
      </div>
    );
  }

  return (
    <div className="pbp-section py-10 md:py-12">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#86868b] font-semibold">Master admin</div>
          <h1 className="mt-1 text-3xl md:text-4xl font-semibold tracking-tight">Control center</h1>
        </div>
        <div className="text-sm text-[#86868b]">
          Signed in as <b className="text-[#1d1d1f]">{user.email}</b>
        </div>
      </div>

      <div className="flex gap-2 border-b border-black/10 mb-8 overflow-x-auto">
        {[
          { id: "overview", l: "Overview" },
          { id: "users", l: "Users" },
          { id: "listings", l: "Listings" },
          { id: "reports", l: "Reports" },
          { id: "audit", l: "Audit log" },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => { setTab(t.id); setQ(""); }}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id ? "border-[#0071e3] text-[#0071e3]" : "border-transparent text-[#86868b] hover:text-[#1d1d1f]"
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {stats ? (
            [
              { l: "Users", v: stats.users, i: Users },
              { l: "Suspended users", v: stats.users_suspended, i: ShieldOff },
              { l: "Active listings", v: stats.listings_active, i: Boxes },
              { l: "Removed listings", v: stats.listings_removed, i: ShieldOff },
              { l: "Chats", v: stats.chats, i: MessagesSquare },
              { l: "Messages", v: stats.messages, i: MessagesSquare },
              { l: "Open reports", v: stats.reports_open, i: Flag },
            ].map(({ l, v, i: Icon }) => (
              <div key={l} className="pbp-card p-5">
                <div className="flex items-center gap-3 text-[#86868b] text-xs uppercase tracking-wider font-semibold">
                  <Icon className="w-4 h-4" strokeWidth={1.6} /> {l}
                </div>
                <div className="mt-3 text-3xl font-semibold tracking-tight">{v}</div>
              </div>
            ))
          ) : (
            <Loader2 className="w-5 h-5 animate-spin text-[#0071e3]" />
          )}
        </div>
      )}

      {(tab === "users" || tab === "listings") && (
        <div className="flex gap-2 mb-5">
          <input
            data-testid="admin-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (tab === "users" ? loadUsers() : loadListings())}
            placeholder={tab === "users" ? "Search by name or email" : "Search by model, description, seller name"}
            className="pbp-input max-w-md"
          />
          <button onClick={() => (tab === "users" ? loadUsers() : loadListings())} className="pbp-btn-secondary">Search</button>
        </div>
      )}

      {tab === "users" && (
        <div className="pbp-card overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="bg-[#f5f5f7] text-left">
              <tr><Th>User</Th><Th>Email</Th><Th>Country</Th><Th>Listings</Th><Th>Status</Th><Th>Actions</Th></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-t border-black/5">
                  <Td>
                    <div className="flex items-center gap-3">
                      {u.picture ? <img src={u.picture} className="w-7 h-7 rounded-full" alt="" /> : <div className="w-7 h-7 rounded-full bg-[#0071e3] grid place-items-center text-white text-xs font-semibold">{u.name?.[0]?.toUpperCase()}</div>}
                      <span className="font-medium">{u.name}</span>
                      {u.is_admin && <span className="pbp-chip bg-[#0071e3]/10 text-[#0071e3]">Admin</span>}
                      {u.is_verified && <BadgeCheck className="w-4 h-4 text-[#0071e3]" strokeWidth={1.8} />}
                    </div>
                  </Td>
                  <Td className="text-[#86868b]">{u.email}</Td>
                  <Td>{u.country || "—"}</Td>
                  <Td>{u.listing_count || 0}</Td>
                  <Td>
                    {u.is_suspended ? <span className="pbp-chip bg-[#ff3b30]/10 text-[#a01d18]">Suspended</span> : <span className="pbp-chip bg-[#34c759]/10 text-[#1c7c3a]">Active</span>}
                  </Td>
                  <Td>
                    <div className="flex gap-2">
                      <button disabled={working || u.is_admin} onClick={() => toggleSuspend(u)} className="pbp-btn-ghost text-[12px] !px-3 !py-1.5" data-testid={`admin-user-suspend-${u.user_id}`}>
                        {u.is_suspended ? "Reinstate" : "Suspend"}
                      </button>
                      <button disabled={working} onClick={() => toggleVerify(u)} className="pbp-btn-ghost text-[12px] !px-3 !py-1.5">
                        <ShieldCheck className="w-3.5 h-3.5" /> {u.is_verified ? "Unverify" : "Verify"}
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
              {users.length === 0 && <tr><Td colSpan={6} className="text-center text-[#86868b] py-8">No users.</Td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "listings" && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="admin-listing-filters">
            {[
              { v: "all", l: "All" },
              { v: "pending_review", l: "Pending review" },
              { v: "active", l: "Active" },
              { v: "removed", l: "Removed" },
              { v: "sold", l: "Sold" },
            ].map((f) => (
              <button
                key={f.v}
                data-testid={`admin-listing-filter-${f.v}`}
                onClick={() => { setListings([]); setListingFilter(f.v); }}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                  listingFilter === f.v
                    ? "border-[#0071e3] bg-[#0071e3] text-white"
                    : "border-[#d2d2d7] bg-white text-[#1d1d1f] hover:border-[#0071e3]"
                }`}
              >
                {f.l}
              </button>
            ))}
          </div>
          <div className="pbp-card overflow-hidden">
            <table className="w-full text-sm" data-testid="admin-listings-table">
              <thead className="bg-[#f5f5f7] text-left">
                <tr><Th>Listing</Th><Th>Seller</Th><Th>Type</Th><Th>Location</Th><Th>Status</Th><Th>Actions</Th></tr>
              </thead>
              <tbody>
                {listings.map((l) => (
                  <React.Fragment key={l.listing_id}>
                    <tr className="border-t border-black/5">
                      <Td>
                        <Link to={`/listing/${l.listing_id}`} className="flex items-center gap-3">
                          {l.images?.[0]?.url && <img src={l.images[0].url} className="w-10 h-10 rounded-md object-cover" alt="" />}
                          <div className="min-w-0">
                            <div className="font-medium truncate">{l.category} · {l.model_number}</div>
                            <div className="text-xs text-[#86868b] truncate">{l.model_name || l.part_name}</div>
                          </div>
                        </Link>
                      </Td>
                      <Td className="text-[#86868b]">{l.user_name}</Td>
                      <Td>{l.seller_type || "—"}</Td>
                      <Td>{l.city}, {l.country}</Td>
                      <Td>
                        <span className={`pbp-chip ${
                          l.status === "active" ? "bg-[#34c759]/10 text-[#1c7c3a]" :
                          l.status === "sold" ? "bg-[#0071e3]/10 text-[#0071e3]" :
                          l.status === "pending_review" ? "bg-[#ff9500]/15 text-[#a05c00]" :
                          "bg-[#ff3b30]/10 text-[#a01d18]"
                        }`}>{l.status.replace("_", " ")}</span>
                      </Td>
                      <Td>
                        {l.status === "pending_review" ? (
                          <div className="flex gap-2">
                            <button
                              disabled={working}
                              onClick={() => setListingStatus(l, "active")}
                              data-testid={`admin-listing-approve-${l.listing_id}`}
                              className="pbp-btn-primary text-[12px] !px-3 !py-1.5"
                            >
                              Approve
                            </button>
                            <button
                              disabled={working}
                              onClick={() => { setRejectingId(l.listing_id); setRejectReason(""); }}
                              data-testid={`admin-listing-reject-${l.listing_id}`}
                              className="pbp-btn-ghost text-[12px] !px-3 !py-1.5 text-[#ff3b30]"
                            >
                              Reject
                            </button>
                          </div>
                        ) : l.status !== "removed" ? (
                          <button disabled={working} onClick={() => setListingStatus(l, "removed")} className="pbp-btn-ghost text-[12px] !px-3 !py-1.5 text-[#ff3b30]" data-testid={`admin-listing-remove-${l.listing_id}`}>Remove</button>
                        ) : (
                          <button disabled={working} onClick={() => setListingStatus(l, "active")} className="pbp-btn-ghost text-[12px] !px-3 !py-1.5">Restore</button>
                        )}
                      </Td>
                    </tr>
                    {l.status === "pending_review" && (
                      <tr className="border-t border-dashed border-black/10 bg-[#ff9500]/5">
                        <Td colSpan={6}>
                          <div className="text-xs text-[#7a3f00] flex flex-col gap-1">
                            {l.last_edit_review ? (
                              <div><b>AI review:</b> {l.last_edit_review.reasoning || "—"} {l.last_edit_review.risk && <span className="ml-2 pbp-chip bg-white">risk: {l.last_edit_review.risk}</span>}</div>
                            ) : (
                              <div><b>Manual review required.</b> No AI review trail (status set directly).</div>
                            )}
                            {rejectingId === l.listing_id && (
                              <div className="mt-2 flex flex-wrap gap-2 items-center" data-testid={`admin-reject-form-${l.listing_id}`}>
                                <input
                                  autoFocus
                                  value={rejectReason}
                                  onChange={(e) => setRejectReason(e.target.value)}
                                  placeholder="Reason for rejecting (visible in audit log)"
                                  className="pbp-input !py-1.5 text-[13px] max-w-md"
                                />
                                <button disabled={working} onClick={confirmReject} data-testid={`admin-reject-confirm-${l.listing_id}`} className="pbp-btn-primary text-[12px] !px-3 !py-1.5">Confirm reject</button>
                                <button onClick={() => setRejectingId(null)} className="pbp-btn-ghost text-[12px] !px-3 !py-1.5">Cancel</button>
                              </div>
                            )}
                          </div>
                        </Td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {listings.length === 0 && <tr><Td colSpan={6} className="text-center text-[#86868b] py-8">No listings.</Td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "reports" && (
        <div className="pbp-card overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-reports-table">
            <thead className="bg-[#f5f5f7] text-left">
              <tr><Th>When</Th><Th>Listing</Th><Th>Reason</Th><Th>Status</Th><Th>Actions</Th></tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.report_id} className="border-t border-black/5">
                  <Td className="text-[#86868b]">{new Date(r.created_at).toLocaleString()}</Td>
                  <Td>{r.listing_id ? <Link to={`/listing/${r.listing_id}`} className="pbp-link">{r.listing_id}</Link> : "—"}</Td>
                  <Td className="max-w-md">{r.reason}</Td>
                  <Td><span className={`pbp-chip ${r.status === "open" ? "bg-[#ff9500]/10 text-[#a05c00]" : "bg-[#34c759]/10 text-[#1c7c3a]"}`}>{r.status}</span></Td>
                  <Td>
                    {r.status === "open" && (
                      <button disabled={working} onClick={() => resolveReport(r)} className="pbp-btn-ghost text-[12px] !px-3 !py-1.5">Mark resolved</button>
                    )}
                  </Td>
                </tr>
              ))}
              {reports.length === 0 && <tr><Td colSpan={5} className="text-center text-[#86868b] py-8">No reports.</Td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {tab === "audit" && (
        <div className="pbp-card overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-audit-table">
            <thead className="bg-[#f5f5f7] text-left">
              <tr><Th>When</Th><Th>Admin</Th><Th>Action</Th><Th>Target</Th><Th>Details</Th></tr>
            </thead>
            <tbody>
              {audit.map((a) => (
                <tr key={a.action_id} className="border-t border-black/5 align-top">
                  <Td className="text-[#86868b] whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</Td>
                  <Td>
                    <div className="font-medium">{a.admin_name || a.admin_email}</div>
                    <div className="text-xs text-[#86868b]">{a.admin_email}</div>
                  </Td>
                  <Td>
                    <span className="pbp-chip bg-[#0071e3]/10 text-[#0071e3]">{a.action}</span>
                  </Td>
                  <Td>
                    <div className="text-xs uppercase tracking-wider text-[#86868b]">{a.target_type}</div>
                    <div className="font-medium truncate max-w-xs">{a.target_label || a.target_id}</div>
                  </Td>
                  <Td>
                    {a.before && a.after && (
                      <div className="text-xs text-[#86868b]">
                        <span className="line-through">{JSON.stringify(a.before)}</span> → <span className="text-[#1d1d1f]">{JSON.stringify(a.after)}</span>
                      </div>
                    )}
                    {a.reason && <div className="text-xs italic mt-1">“{a.reason}”</div>}
                  </Td>
                </tr>
              ))}
              {audit.length === 0 && <tr><Td colSpan={5} className="text-center text-[#86868b] py-8">No admin actions yet.</Td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-[#86868b]">{children}</th>;
const Td = ({ children, className = "", ...props }) => <td className={`px-4 py-3 align-middle ${className}`} {...props}>{children}</td>;
